import type React from 'react';
import { useState, type ReactNode } from 'react';
import {
  Box,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  InputBase,
  Drawer,
  Chip,
} from '@mui/material';
import { SearchIcon, MenuIcon } from 'lucide-react';
import { Sidebar } from './Sidebar';
import { SignInButton } from '../auth/SignInButton';
import { getHeaderColor, getEnvironmentName } from '../../lib/utils';

interface AppLayoutProps {
  children: ReactNode;
  title?: string;
  showSearch?: boolean;
  onSearch?: (query: string) => void;
  /** コンテンツ領域の余白(p:3)を無効化し、全画面レイアウトのページが自前で領域を使えるようにする */
  disableContentPadding?: boolean;
}

const DRAWER_WIDTH = 260;

export function AppLayout({
  children,
  title = '顧客管理ダッシュボード',
  showSearch = true,
  onSearch,
  disableContentPadding = false,
}: AppLayoutProps) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const toggleDrawer = () => {
    setDrawerOpen(!drawerOpen);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value;
    setSearchQuery(query);
  };

  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      if (event.nativeEvent.isComposing) {
        return;
      }
      onSearch?.(searchQuery);
    }
  };

  const handleSearchIconClick = () => {
    onSearch?.(searchQuery);
  };

  const headerColor = getHeaderColor();
  const environmentName = getEnvironmentName();

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: '#f8fafc' }}>
      {/* Toolbar の実効高はデスクトップ既定 64px（MUI 既定 mixins.toolbar 準拠）。
          globals.css の --app-header-h(64px) はこの実効高を前提にしており、
          変更する場合は両方を合わせて更新する */}
      <AppBar
        position="fixed"
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backgroundColor: headerColor,
          color: '#ffffff',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={toggleDrawer}
            sx={{ mr: 2, color: 'inherit' }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexGrow: 1 }}>
            <Typography
              variant="h6"
              noWrap
              component="div"
              sx={{ fontWeight: 700, color: 'inherit' }}
            >
              {title}
            </Typography>
            <Chip
              label={environmentName}
              size="small"
              sx={{
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                color: '#ffffff',
                fontWeight: 600,
                fontSize: '0.7rem',
                height: '22px',
              }}
            />
          </Box>
          {showSearch && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                borderRadius: '8px',
                px: 1,
                mr: 2,
                transition: 'all 0.2s ease',
                '&:focus-within': {
                  backgroundColor: 'rgba(255, 255, 255, 0.25)',
                },
              }}
            >
              <IconButton
                size="small"
                sx={{ p: 0.5, color: 'rgba(255, 255, 255, 0.7)' }}
                onClick={handleSearchIconClick}
              >
                <SearchIcon size={18} />
              </IconButton>
              <InputBase
                placeholder="検索... (Enterキーで実行)"
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyDown={handleSearchKeyDown}
                sx={{
                  ml: 0.5,
                  color: '#ffffff',
                  fontSize: '0.875rem',
                  '& input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.6)',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          )}
          <SignInButton />
        </Toolbar>
      </AppBar>

      <Drawer
        variant="temporary"
        open={drawerOpen}
        onClose={toggleDrawer}
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          [`& .MuiDrawer-paper`]: {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            backgroundColor: '#ffffff',
          },
        }}
      >
        <Sidebar />
      </Drawer>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: disableContentPadding ? 0 : 3,
          width: { sm: `calc(100% - ${DRAWER_WIDTH}px)` },
          // AppBar の実効高と揃える。マジック値の重複を避けるため
          // globals.css の --app-header-h を単一の定義源として参照する
          // （CustomDashboardPresentation も同変数を参照）
          mt: 'var(--app-header-h)',
          bgcolor: '#f8fafc',
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
